/**
 * Task comments (spec §6.5, item 6): the latest two with Show all, then the
 * composer.
 */
import { useCallback, useMemo, useState } from "react";
import { ImageLightbox, PocketSection } from "@/components/ui";
import { useT } from "@/i18n";
import { panelsTaskCommentsMessages } from "@/i18n/messages/panels-task-comments";
import { useAgentStore } from "@/stores/agent-store";
import { dedupeTaskComments } from "./task-comment-dedupe";
import { TaskCommentComposer } from "./task-comment-composer";
import { TaskCommentItem } from "./task-comment-item";
import type { TaskCommentSectionProps } from "./task-comment-section-types";

const LATEST_COUNT = 2;

export function TaskCommentSection({
  projectId,
  taskId,
  comments,
  onCommentsChange,
}: TaskCommentSectionProps) {
  const t = useT(panelsTaskCommentsMessages);
  const projectAgents = useAgentStore((s) => s.agents);
  const [showAll, setShowAll] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const visibleComments = useMemo(
    () => dedupeTaskComments(comments),
    [comments],
  );
  const hiddenCount = showAll
    ? 0
    : Math.max(0, visibleComments.length - LATEST_COUNT);
  const shownComments =
    hiddenCount > 0 ? visibleComments.slice(-LATEST_COUNT) : visibleComments;

  const resolveAgentName = useCallback(
    (agentId: string | null): string => {
      if (!agentId) return t("you");
      return (
        projectAgents.find((agent) => agent.agent_template === agentId)
          ?.template_name ?? t("agent")
      );
    },
    [projectAgents, t],
  );

  return (
    <PocketSection
      title={visibleComments.length > 0 ? t("comments") : undefined}
      action={
        hiddenCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="rounded-pill px-2 text-appkit-caption font-medium normal-case tracking-normal text-muted transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            data-testid="task-pocket-comments-show-all"
          >
            {t("showAll")}
          </button>
        ) : undefined
      }
      testId="task-pocket-comments"
    >
      {shownComments.map((comment) => (
        <TaskCommentItem
          key={comment.id}
          comment={comment}
          projectId={projectId}
          resolveAgentName={resolveAgentName}
          onImageClick={setLightboxSrc}
        />
      ))}
      <TaskCommentComposer
        projectId={projectId}
        taskId={taskId}
        comments={visibleComments}
        onCommentsChange={onCommentsChange}
      />
      {lightboxSrc ? (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      ) : null}
    </PocketSection>
  );
}
